<template>
  <section class="form">
    <card-component title="Capture Project Admin Details" icon="ballot">
      <form @submit.prevent="submit">
        <b-field label="Authentication" horizontal>
          <b-field>
            <b-input
              icon="email"
              type="email"
              v-model="form.email"
              placeholder="Email"
              name="email"
              required
            />
          </b-field>
          <b-field>
            <b-input
              icon="alert-octagram"
              type="password"
              v-model="form.password"
              placeholder="Password"
              name="password"
              required
            />
          </b-field>
        </b-field>
        <b-field label="First Name" horizontal>
          <b-input
            icon="account"
            v-model="form.firstName"
            placeholder="Sarah"
            name="name"
            required
          />
        </b-field>
        <b-field label="Learner Facing Name" horizontal>
          <b-input
            icon="account"
            v-model="form.learnerFacingName"
            placeholder="Mrs Smith"
            name="learnerFacingName"
          />
        </b-field>
        <b-field label="Title" horizontal>
          <b-input
            icon="account"
            v-model="form.title"
            placeholder="Class Teacher (if applicable)"
            name="title"
          />
        </b-field>
        <b-field label="Avatar URL" horizontal>
          <b-input
            icon="account"
            v-model="form.avatar"
            placeholder="Avatar URL (if applicable)"
            name="avatar"
          />
          <b-button
            class="control"
            type="is-primary is-outlined"
            @click="openAvatarUrl()"
            >Find Avatar</b-button
          >
        </b-field>
      </form>
    </card-component>
    <card-component
      title="Project Admin Permissions"
      subtitle="Select the appropriate levels and roles for this admin"
      icon="ballot-outline"
    >
      <div v-if="!projectStructure.project">
        <div>
          Select a project to load the structure
        </div>
        <hr />
        <b-field label="Project" horizontal>
          <b-select
            placeholder="Select a project"
            v-model="form.project"
            required
          >
            <option
              v-for="(project, index) in projects"
              :key="index"
              :value="project.project"
            >
              {{ project.project }}
            </option>
          </b-select>
        </b-field>
        <b-field horizontal>
          <b-field grouped>
            <div class="control">
              <b-button
                :type="form.project ? 'is-primary' : 'is-primary is-outlined'"
                @click="populateProjectStructure(form.project)"
                >Load project structure</b-button
              >
            </div>
            <div class="control">
              <b-button type="is-primary is-outlined" @click="reset"
                >Reset</b-button
              >
            </div>
          </b-field>
        </b-field>
      </div>
      <div v-if="projectStructure.project && customElementsFormFilled">
        <b-field label="Project Level" horizontal>
          <b-field>
            <b-switch
              v-model="customElementsForm.projectLevelSwitch"
              type="is-success"
              @input="unselectProjectLevelRole()"
            >
              {{ projectStructure.projectName }}
            </b-switch>
          </b-field>

          <b-field grouped position="is-right">
            <div class="control">
              <b-button
                v-model="customElementsForm.projectLevelSelected"
                :type="
                  customElementsForm.projectLevelSelected == 'super-admin'
                    ? 'is-primary is-success'
                    : 'is-primary is-success is-outlined'
                "
                :disabled="!customElementsForm.projectLevelSwitch"
                native-value="super-admin"
                @click="selectProjectLevelRole('super-admin')"
                >Super-admin</b-button
              >
            </div>
            <div class="control">
              <b-button
                v-model="customElementsForm.projectLevelSelected"
                :type="
                  customElementsForm.projectLevelSelected == 'admin'
                    ? 'is-primary is-success'
                    : 'is-primary is-success is-outlined'
                "
                :disabled="!customElementsForm.projectLevelSwitch"
                native-value="admin"
                @click="selectProjectLevelRole('admin')"
                >Admin</b-button
              >
            </div>
            <div class="control">
              <b-button
                v-model="customElementsForm.projectLevelSelected"
                :type="
                  customElementsForm.projectLevelSelected == 'comms'
                    ? 'is-primary is-success'
                    : 'is-primary is-success is-outlined'
                "
                :disabled="!customElementsForm.projectLevelSwitch"
                native-value="comms"
                @click="selectProjectLevelRole('comms')"
                >Comms</b-button
              >
            </div>
            <div class="control">
              <b-button
                v-model="customElementsForm.projectLevelSelected"
                :type="
                  customElementsForm.projectLevelSelected == 'read-only'
                    ? 'is-primary is-success'
                    : 'is-primary is-success is-outlined'
                "
                :disabled="!customElementsForm.projectLevelSwitch"
                native-value="read-only"
                @click="selectProjectLevelRole('read-only')"
                >Read-only</b-button
              >
            </div>
          </b-field>
        </b-field>
        <hr />

        <div v-for="(school, index) in projectStructure.schools" :key="index">
          <b-field
            v-if="customElementsForm.schoolLevelSwitches"
            label="School Level"
            horizontal
          >
            <b-field>
              <b-switch
                v-model="customElementsForm.schoolLevelSwitches[index]"
                type="is-success"
                @input="unselectSchoolLevelRole(index)"
              >
                {{ school.name }}
              </b-switch>
            </b-field>

            <b-field grouped position="is-right">
              <div class="control">
                <b-button
                  v-model="customElementsForm.schoolLevelsSelected[index]"
                  :type="
                    customElementsForm.schoolLevelsSelected[index] == 'admin'
                      ? 'is-primary is-success'
                      : 'is-primary is-success is-outlined'
                  "
                  :disabled="!isSchoolLevelDisabled(index)"
                  native-value="admin"
                  @click="selectSchoolLevelRole('admin', index)"
                  >Admin</b-button
                >
              </div>
              <div class="control">
                <b-button
                  v-model="customElementsForm.schoolLevelsSelected[index]"
                  :type="
                    customElementsForm.schoolLevelsSelected[index] == 'comms'
                      ? 'is-primary is-success'
                      : 'is-primary is-success is-outlined'
                  "
                  :disabled="!isSchoolLevelDisabled(index)"
                  native-value="comms"
                  @click="selectSchoolLevelRole('comms', index)"
                  >Comms</b-button
                >
              </div>
              <div class="control">
                <b-button
                  v-model="customElementsForm.schoolLevelsSelected[index]"
                  :type="
                    customElementsForm.schoolLevelsSelected[index] ==
                    'read-only'
                      ? 'is-primary is-success'
                      : 'is-primary is-success is-outlined'
                  "
                  :disabled="!isSchoolLevelDisabled(index)"
                  native-value="read-only"
                  @click="selectSchoolLevelRole('read-only', index)"
                  >Read-only</b-button
                >
              </div>
            </b-field>
          </b-field>
          <hr />

          <div v-for="(grade, gradeKey) in school.grades" :key="gradeKey">
            <b-field label="Grade Level" horizontal>
              <b-field>
                <b-switch
                  v-model="
                    customElementsForm.gradeLevelSwitches[index][gradeKey]
                  "
                  type="is-success"
                  @input="unselectGradeLevelRole(index, gradeKey)"
                >
                  Grade {{ gradeKey }}
                </b-switch>
              </b-field>

              <b-field grouped position="is-right">
                <div class="control">
                  <b-button
                    v-model="
                      customElementsForm.gradeLevelsSelected[index][gradeKey]
                    "
                    :type="
                      customElementsForm.gradeLevelsSelected[index][gradeKey] ==
                      'admin'
                        ? 'is-primary is-success'
                        : 'is-primary is-success is-outlined'
                    "
                    :disabled="!isGradeLevelDisabled(index, gradeKey)"
                    native-value="admin"
                    @click="selectGradeLevelRole('admin', index, gradeKey)"
                    >Admin</b-button
                  >
                </div>
                <div class="control">
                  <b-button
                    v-model="
                      customElementsForm.gradeLevelsSelected[index][gradeKey]
                    "
                    :type="
                      customElementsForm.gradeLevelsSelected[index][gradeKey] ==
                      'comms'
                        ? 'is-primary is-success'
                        : 'is-primary is-success is-outlined'
                    "
                    :disabled="!isGradeLevelDisabled(index, gradeKey)"
                    @click="selectGradeLevelRole('comms', index, gradeKey)"
                    native-value="comms"
                    >Comms</b-button
                  >
                </div>
                <div class="control">
                  <b-button
                    v-model="
                      customElementsForm.gradeLevelsSelected[index][gradeKey]
                    "
                    :type="
                      customElementsForm.gradeLevelsSelected[index][gradeKey] ==
                      'read-only'
                        ? 'is-primary is-success'
                        : 'is-primary is-success is-outlined'
                    "
                    :disabled="!isGradeLevelDisabled(index, gradeKey)"
                    @click="selectGradeLevelRole('read-only', index, gradeKey)"
                    native-value="read-only"
                    >Read-only</b-button
                  >
                </div>
              </b-field>
            </b-field>

            <hr />
            <div v-for="(clss, classIndex) in grade.classes" :key="classIndex">
              <b-field label="Class Level" horizontal>
                <b-field>
                  <b-switch
                    v-model="
                      customElementsForm.classLevelSwitches[index][gradeKey][
                        classIndex
                      ]
                    "
                    type="is-success"
                    @input="unselectClassLevelRole(index, gradeKey, classIndex)"
                  >
                    Class {{ clss }}
                  </b-switch>
                </b-field>

                <b-field grouped position="is-right">
                  <div class="control">
                    <b-button
                      v-model="
                        customElementsForm.classLevelsSelected[index][gradeKey][
                          classIndex
                        ]
                      "
                      :type="
                        customElementsForm.classLevelsSelected[index][gradeKey][
                          classIndex
                        ] == 'admin'
                          ? 'is-primary is-success'
                          : 'is-primary is-success is-outlined'
                      "
                      :disabled="
                        !isClassLevelDisabled(index, gradeKey, classIndex)
                      "
                      native-value="admin"
                      @click="
                        selectClassLevelRole(
                          'admin',
                          index,
                          gradeKey,
                          classIndex
                        )
                      "
                      >Admin</b-button
                    >
                  </div>
                  <div class="control">
                    <b-button
                      v-model="
                        customElementsForm.classLevelsSelected[index][gradeKey][
                          classIndex
                        ]
                      "
                      :type="
                        customElementsForm.classLevelsSelected[index][gradeKey][
                          classIndex
                        ] == 'comms'
                          ? 'is-primary is-success'
                          : 'is-primary is-success is-outlined'
                      "
                      :disabled="
                        !isClassLevelDisabled(index, gradeKey, classIndex)
                      "
                      native-value="comms"
                      @click="
                        selectClassLevelRole(
                          'comms',
                          index,
                          gradeKey,
                          classIndex
                        )
                      "
                      >Comms</b-button
                    >
                  </div>
                  <div class="control">
                    <b-button
                      v-model="
                        customElementsForm.classLevelsSelected[index][gradeKey][
                          classIndex
                        ]
                      "
                      :type="
                        customElementsForm.classLevelsSelected[index][gradeKey][
                          classIndex
                        ] == 'read-only'
                          ? 'is-primary is-success'
                          : 'is-primary is-success is-outlined'
                      "
                      :disabled="
                        !isClassLevelDisabled(index, gradeKey, classIndex)
                      "
                      @click="
                        selectClassLevelRole(
                          'read-only',
                          index,
                          gradeKey,
                          classIndex
                        )
                      "
                      native-value="read-only"
                      >Read-only</b-button
                    >
                  </div>
                </b-field>
              </b-field>
            </div>
            <hr />
          </div>
        </div>
      </div>
    </card-component>
    <card-component
      v-if="projectStructure.project"
      title="Ready to submit"
      icon="check-box-outline"
    >
      <div v-if="presubmit">
        <b-field label="Authentication" horizontal>
          <b-field
            icon="email"
            type="email"
            v-model="form.email"
            placeholder="Email"
            name="email"
          >
            <div v-html="form.email"></div>
          </b-field>
          <b-field
            icon="alert-octagram"
            type="password"
            v-model="form.password"
            placeholder="Password"
            name="password"
          >
            <div v-html="form.password"></div>
          </b-field>
        </b-field>
        <b-field label="Personal" horizontal>
          <b-field
            icon="account"
            v-model="form.firstName"
            placeholder="Sarah"
            name="name"
          >
            <div v-html="form.firstName"></div>
          </b-field>
        </b-field>
        <b-field label="Learner Facing" horizontal>
          <b-field
            icon="account"
            v-model="form.learnerFacingName"
            placeholder="Mrs Smith (if applicable)"
            name="learnerFacingName"
          >
            <div v-html="form.learnerFacingName"></div>
          </b-field>
          <b-field
            icon="account"
            v-model="form.title"
            placeholder="Class Teacher (if applicable)"
            name="title"
          >
            <div v-html="form.title"></div>
          </b-field>
        </b-field>
        <b-field label="Avatar" horizontal>
          <b-field
            icon="account"
            v-model="form.avatar"
            placeholder="Avatar URL (if applicable)"
            name="avatar"
          >
            <div v-html="form.avatar"></div>
          </b-field>
        </b-field>

        <b-field label="Project" horizontal>
          <b-field placeholder="Select a project" v-model="form.project">
            <div v-html="projectStructure.projectName"></div>
          </b-field>
          <div v-if="form.projectLevels.isSuperAdmin">Is Super Admin</div>

          <b-field
            v-if="form.projectLevels.admin.length > 0"
            label="Admin Permissions: "
          >
            <ul>
              <li
                v-for="(role, grIndex) in form.projectLevels.admin"
                :key="grIndex"
                v-html="permissionsDisplay(role, grIndex)"
              ></li>
            </ul>
          </b-field>
          <b-field
            v-if="form.projectLevels.comms.length > 0"
            label="Comms Permissions: "
          >
            <ul>
              <li
                v-for="(role, grIndex) in form.projectLevels.comms"
                :key="grIndex"
                v-html="permissionsDisplay(role, grIndex)"
              ></li>
            </ul>
          </b-field>
          <b-field
            v-if="form.projectLevels['read-only'].length > 0"
            label="Read-only Permissions: "
          >
            <ul>
              <li
                v-for="(role, grIndex) in form.projectLevels['read-only']"
                :key="grIndex"
                v-html="permissionsDisplay(role, grIndex)"
              ></li>
            </ul>
          </b-field>
        </b-field>
      </div>
      <div v-else>
        Click to load project admin configuration. Once you're happy, press
        submit.
      </div>
      <hr />
      <b-field horizontal>
        <b-field grouped>
          <div class="control">
            <b-button
              :type="
                form.project
                  ? 'is-primary is-success'
                  : 'is-primary is-success is-outlined'
              "
              @click="loadConfig()"
              v-html="
                presubmit ? 'Refresh configuration' : 'Preview configuration'
              "
            />
          </div>
        </b-field>
        <b-field v-if="presubmit" grouped>
          <div class="control">
            <b-button
              :type="'is-primary is-success'"
              @click="submit()"
              v-html="'Submit'"
            />
          </div>
          <div
            v-if="submitMessage"
            v-html="submitMessage.message"
            :style="submitMessage.submitted ? 'color: green;' : 'color: red;'"
          ></div>
        </b-field>
        <b-field grouped>
          <div class="control">
            <b-button
              :type="'is-primary is-success is-outlined'"
              @click="reset()"
              v-html="'Reset all'"
            />
          </div>
        </b-field>
      </b-field>
    </card-component>
  </section>
</template>

<script>
import CardComponent from "@/components/CardComponent";
import mapValues from "lodash/mapValues";
import { mapState, mapActions } from "vuex";
import Vue from "vue";

export default {
  name: "Forms",
  components: {
    CardComponent
  },
  data() {
    return {
      isLoading: false,
      presubmit: false,
      submitMessage: null,
      avatarUrl: null,
      form: {
        email: null,
        password: null,
        firstName: null,
        project: null,
        title: null,
        learnerFacingName: null,
        avatar: null,
        projectLevels: {
          isSuperAdmin: false,
          comms: [],
          admin: [],
          "read-only": []
        }
      },
      customElementsForm: {
        projectLevelSwitch: false,
        projectLevelSelected: "",
        schoolLevelSwitches: [],
        gradeLevelSwitches: [],
        classLevelSwitches: [],
        schoolLevelsSelected: [],
        gradeLevelsSelected: [],
        classLevelsSelected: []
      },
      customElementsFormFilled: false
    };
  },
  mounted() {
    // this.avatarUrl = process.env.VUE_APP_AVATAR_URL; why does'nt this woorkkkkkkk
    this.avatarUrl =
      "https://console.cloud.google.com/storage/browser/reflective-learning-prod.appspot.com/avatars/pa";
  },
  computed: {
    ...mapState({
      user: "user",
      projects: state => state.globalStore.projects,
      projectStructure: state => state.adminStore.projectStructure
    })
  },
  methods: {
    ...mapActions({
      fetchProjectStructure: "adminStore/fetchProjectStructure",
      submitProjectAdmin: "adminStore/submitProjectAdmin"
    }),
    isSchoolLevelDisabled(index) {
      return this.customElementsForm.schoolLevelSwitches[index];
    },
    isGradeLevelDisabled(index, key) {
      return this.customElementsForm.gradeLevelSwitches[index][key];
    },
    isClassLevelDisabled(index, gradeKey, key) {
      return this.customElementsForm.classLevelSwitches[index][gradeKey][key];
    },
    async populateProjectStructure(project) {
      await this.fetchProjectStructure(project);
      this.projectStructure.schools.forEach((school, schoolIndex) => {
        this.customElementsForm.schoolLevelSwitches.push(false);
        this.customElementsForm.schoolLevelsSelected.push("");

        if (school.grades) {
          this.customElementsForm.gradeLevelSwitches.push({});
          this.customElementsForm.classLevelSwitches.push({});
          this.customElementsForm.gradeLevelsSelected.push({});
          this.customElementsForm.classLevelsSelected.push({});
          Object.keys(school.grades).forEach(gradeKey => {
            Vue.set(
              this.customElementsForm.gradeLevelSwitches[schoolIndex],
              gradeKey,
              false
            );
            Vue.set(
              this.customElementsForm.gradeLevelsSelected[schoolIndex],
              gradeKey,
              ""
            );
            if (school.grades[gradeKey].classes) {
              Vue.set(
                this.customElementsForm.classLevelSwitches[schoolIndex],
                gradeKey,
                []
              );
              Vue.set(
                this.customElementsForm.classLevelsSelected[schoolIndex],
                gradeKey,
                []
              );

              Object.keys(school.grades[gradeKey].classes).forEach(() => {
                this.customElementsForm.classLevelSwitches[schoolIndex][
                  gradeKey
                ].push(false);
                this.customElementsForm.classLevelsSelected[schoolIndex][
                  gradeKey
                ].push("");
              });
            }
          });
        }
      });
      this.customElementsFormFilled = true;
    },
    openAvatarUrl() {
      window.open(this.avatarUrl);
    },
    selectProjectLevelRole(role) {
      this.customElementsForm.projectLevelSelected = role;
    },
    selectSchoolLevelRole(role, index) {
      Vue.set(this.customElementsForm.schoolLevelsSelected, index, role);
    },
    selectGradeLevelRole(role, index, grade) {
      Vue.set(this.customElementsForm.gradeLevelsSelected[index], grade, role);
    },
    selectClassLevelRole(role, index, gradeKey, clss) {
      Vue.set(
        this.customElementsForm.classLevelsSelected[index][gradeKey],
        clss,
        role
      );
    },
    unselectProjectLevelRole() {
      if (!this.customElementsForm.projectLevelSwitch)
        this.customElementsForm.projectLevelSelected = "";
    },
    unselectSchoolLevelRole(index) {
      if (!this.customElementsForm.schoolLevelSwitches[index])
        Vue.set(this.customElementsForm.schoolLevelsSelected, index, "");
    },
    unselectGradeLevelRole(index, grade) {
      if (!this.customElementsForm.gradeLevelSwitches[index][grade])
        Vue.set(this.customElementsForm.gradeLevelsSelected[index], grade, "");
    },
    unselectClassLevelRole(index, gradeKey, clss) {
      if (!this.customElementsForm.classLevelSwitches[index][gradeKey][clss])
        Vue.set(
          this.customElementsForm.classLevelsSelected[index][gradeKey],
          clss,
          ""
        );
    },
    loadConfig() {
      // RESET
      this.form.projectLevels = {
        isSuperAdmin: false,
        comms: [],
        admin: [],
        "read-only": []
      };
      this.presubmit = true;
      this.submitMessage = null;
      // CONVERT PROJECT LEVEL
      if (this.customElementsForm.projectLevelSelected === "super-admin") {
        this.form.projectLevels.isSuperAdmin = true;
      } else if (this.customElementsForm.projectLevelSelected !== "") {
        this.form.projectLevels.isSuperAdmin = false;
        this.form.projectLevels[
          this.customElementsForm.projectLevelSelected
        ].push({
          isProjectLevel: true
        });
      }

      // CONVERT SCHOOL LEVEL
      for (let schoolIndex in this.customElementsForm.schoolLevelsSelected) {
        let schoolId = this.projectStructure.schools[schoolIndex].schoolId;

        if (
          this.form.projectLevels[
            this.customElementsForm.schoolLevelsSelected[schoolIndex]
          ]
        ) {
          this.form.projectLevels[
            this.customElementsForm.schoolLevelsSelected[schoolIndex]
          ].push({
            schoolId: schoolId,
            isSchoolLevel: true
          });
        }

        // CONVERT GRADE LEVEL
        for (let gradeKey of Object.keys(
          this.customElementsForm.gradeLevelsSelected[schoolIndex]
        )) {
          if (
            this.customElementsForm.gradeLevelsSelected[schoolIndex][
              gradeKey
            ] !== ""
          ) {
            if (
              !this.form.projectLevels[
                this.customElementsForm.gradeLevelsSelected[schoolIndex][
                  gradeKey
                ]
              ].find(item => item.schoolId == schoolId)
            ) {
              this.form.projectLevels[
                this.customElementsForm.gradeLevelsSelected[schoolIndex][
                  gradeKey
                ]
              ].push({
                schoolId: schoolId,
                isSchoolLevel: false,
                grades: [gradeKey]
              });
            } else {
              if (
                !this.form.projectLevels[
                  this.customElementsForm.gradeLevelsSelected[schoolIndex][
                    gradeKey
                  ]
                ].find(item => item.schoolId == schoolId).grades
              ) {
                this.form.projectLevels[
                  this.customElementsForm.gradeLevelsSelected[schoolIndex][
                    gradeKey
                  ]
                ].find(item => item.schoolId == schoolId).grades = [gradeKey];
              } else {
                this.form.projectLevels[
                  this.customElementsForm.gradeLevelsSelected[schoolIndex][
                    gradeKey
                  ]
                ]
                  .find(item => item.schoolId == schoolId)
                  .grades.push(gradeKey);
              }
            }
          }

          for (let classIndex in this.customElementsForm.classLevelsSelected[
            schoolIndex
          ][gradeKey]) {
            if (
              this.customElementsForm.classLevelsSelected[schoolIndex][
                gradeKey
              ][classIndex] !== ""
            ) {
              let classKey = this.projectStructure.schools.find(
                school => school.schoolId === schoolId
              ).grades[gradeKey].classes[classIndex];

              if (
                !this.form.projectLevels[
                  this.customElementsForm.classLevelsSelected[schoolIndex][
                    gradeKey
                  ][classIndex]
                ].find(item => item.schoolId == schoolId)
              ) {
                this.form.projectLevels[
                  this.customElementsForm.classLevelsSelected[schoolIndex][
                    gradeKey
                  ][classIndex]
                ].push({
                  schoolId: schoolId,
                  isSchoolLevel: false,
                  classes: [classKey]
                });
              } else {
                if (
                  !this.form.projectLevels[
                    this.customElementsForm.classLevelsSelected[schoolIndex][
                      gradeKey
                    ][classIndex]
                  ].find(item => item.schoolId == schoolId).classes
                ) {
                  this.form.projectLevels[
                    this.customElementsForm.classLevelsSelected[schoolIndex][
                      gradeKey
                    ][classIndex]
                  ].find(item => item.schoolId == schoolId).classes = [
                    classKey
                  ];
                } else {
                  this.form.projectLevels[
                    this.customElementsForm.classLevelsSelected[schoolIndex][
                      gradeKey
                    ][classIndex]
                  ]
                    .find(item => item.schoolId == schoolId)
                    .classes.push(classKey);
                }
              }
            }
          }
        }
      }

      this.presubmit = true;
    },
    permissionsDisplay(role, index) {
      let permString = "";
      if (index < 1) {
        if (role.isProjectLevel) permString += "Project Level<br>";
        if (role.isSchoolLevel) permString += "School Level<br>";
      }
      if (role.grades && role.grades.length > 0)
        permString +=
          "<b>Grade Level for:</b><br>Grade " +
          role.grades.join(",<br>&#09; Grade ") +
          "<br>";
      if (role.classes && role.classes.length > 0)
        permString +=
          "<b>Class Level for:</b><br>Class " +
          role.classes.join(",<br>&#09; Class ") +
          "<br>";
      return permString;
    },
    async submit() {
      this.submitMessage = null;
      this.submitMessage = await this.submitProjectAdmin(this.form);
    },
    reset() {
      Object.assign(this.form, {
        email: null,
        password: null,
        firstName: null,
        project: null,
        title: null,
        learnerFacingName: null,
        avatar: null,
        projectLevels: {
          isSuperAdmin: false,
          comms: [],
          admin: [],
          "read-only": []
        }
      });
      this.submitMessage = null;
      Object.assign(this.projectStructure, {
        project: null,
        name: "",
        schools: []
      });
      this.presubmit = false;
      Object.assign(this.customElementsForm, {
        projectLevelSwitch: false,
        projectLevelSelected: "",
        schoolLevelSwitches: [],
        gradeLevelSwitches: [],
        classLevelSwitches: [],
        schoolLevelsSelected: [],
        gradeLevelsSelected: [],
        classLevelsSelected: []
      });
      this.customElementsFormFilled = false;
    }
  }
};
</script>
